import { Grid, Typography, CircularProgress, Divider } from '@mui/material';
import { Stack } from '@mui/system';

import { useState, useEffect, useCallback, useRef } from 'react';

import { useSelector, useDispatch } from 'react-redux';

import { useParams, Link, useLocation } from 'react-router-dom';

import AIFeedback from 'common/components/AIFeedback/AIFeedback';
import Banner from 'common/components/Banner/Banner';
import Breadcrumbs from 'common/components/Breadcrumbs/Breadcrumbs';
import StepperProgressBar from 'common/components/StepperProgressBar/StepperProgressBar';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import {
	FEEDBACK_OBJECT_TYPE,
	MODULES,
	TABLE_VIEW_PAGE_SIZE,
} from 'common/constants';
import { AI_SIMULATION_GENAI_DISCLAIMER } from 'common/constants/AISimulationConstants';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { rrcpDashboardRoutes } from 'common/constants/rrcp-dashboard-routes';
import {
	APP_GENERAL_ERROR_MSG,
	APP_GENERIC_BAD_REQUEST_MSG,
	HorizonScanningPageTitle,
	appPages,
} from 'common/constants/TextContent';
import { RootState } from 'common/store/AppStore';
import {
	setIsRelationshipMappingDeleted,
	setToastMessage,
} from 'common/store/reducer/commonReducer';
import {
	setCurrentWorkflowStatus,
	setShouldHorizonDetailRefresh,
} from 'common/store/reducer/horizonScanReducer';
import {
	scrollToTop,
	capitalizeEachWord,
	urlWithTenant,
} from 'common/utils/utilFunctions';

import useTableQuery from 'hooks/useTableQuery';

import { getRegSummaryDetailsData, getWorkflowData } from '../api/index';
import { IRegDetailsResponse, ITaskTabContent } from '../api/type-def';
import ActionPlanLayout from '../components/ActionPlanLayout/ActionPlanLayout';
import { setSelectedEntityTypeData } from '../components/ActionPlanLayout/ActionPlanTaskTableModules';
import AISummary from '../components/AISummary/AISummary';
import CommentsSection from '../components/CommentsSection/CommentsSection';
import DocumentSection from '../components/DocumentSection/DocumentSection';
import RegHeader from '../components/RegHeader/RegHeader';
import {
	getHorizonProgressBarElements,
	regHeaders,
	TITLE_LENGTH,
} from '../HorizonScanConstants';
import horizonCommonStyles from '../HorizonScanning.module.scss';

import styles from './HorizonDetailLayout.module.scss';

const { BAD_REQUEST, NOT_FOUND, OK } = HTTP_STATUS_CODES;
const { HOME_PAGE } = appPages;
const { parent, horizonScanning } = rrcpDashboardRoutes;
const { TABLE_SIZE_12 } = TABLE_VIEW_PAGE_SIZE;
const { DISCLAIMER } = AI_SIMULATION_GENAI_DISCLAIMER;
const { HORIZON_OBLIGATION_SUMMARY } = FEEDBACK_OBJECT_TYPE;
const { HORIZON_SCAN_AND_ALERTS } = MODULES;

const HorizonDetailLayout = () => {
	const { subPartId } = useParams();
	const dispatch = useDispatch();
	const location = useLocation();

	const [isRegDataLoading, setIsRegDataLoading] = useState<boolean>(true);

	const { isToastOpen, message, severity } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);

	const shouldHorizonDetailRefresh: boolean = useSelector(
		(state: RootState) => state?.horizonScan?.shouldHorizonDetailRefresh,
	);

	const taskRowData = useSelector(
		(state: RootState) => state.common.taskRowData,
	);

	const taskTableMappedData = useSelector(
		(state: RootState) => state.common.taskTableMappedData,
	);

	const isSummaryRegenerated = useSelector(
		(state: RootState) => state.horizonScan.isAISummaryRegenerated,
	);

	useEffect(() => {
		if (shouldHorizonDetailRefresh) {
			makeAPICall(true);
		}
	}, [shouldHorizonDetailRefresh]);

	const [regDetailsData, setRegDetailsData] = useState<IRegDetailsResponse>();

	const [loading, setIsLoading] = useState<boolean>(false);

	const [openCommentBox, setOpenCommentBox] = useState<boolean>(false);

	const loggedInUser = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);

	const { updateTableQuery } = useTableQuery({
		id: 'horizonRelationshipMappingCard',
		defaultPageSize: TABLE_SIZE_12,
	});
	const commentsSectionRef = useRef<HTMLDivElement>(null);

	const scrollToCommentsSection = () => {
		commentsSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
	};
	const fetchRegDetailsData = async () => {
		setIsLoading(true);
		setIsRegDataLoading(true);
		const res = await getRegSummaryDetailsData(subPartId || '');
		setIsRegDataLoading(false);
		if (res?.statusCode === OK) {
			setRegDetailsData(res?.responseBody);
			setIsLoading(false);
		}
		if (res?.errorBody || res?.error) {
			const { errorBody } = res;
			const responseError: any = res.error;
			const errorMessage =
				(errorBody?.errorMessage as string) ||
				(responseError?.message as string);
			dispatch(
				setToastMessage({
					message: errorMessage,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
		setIsLoading(false);
	};

	const fetchWorkflowData = async () => {
		setIsLoading(true);
		const responseFromApi = await getWorkflowData(subPartId || '');
		dispatch(setShouldHorizonDetailRefresh(false));

		if (responseFromApi?.statusCode === OK) {
			if (
				responseFromApi?.responseBody &&
				responseFromApi.responseBody.length > 0
			) {
				dispatch(
					setCurrentWorkflowStatus(responseFromApi.responseBody[0].status),
				);
			}
			setIsLoading(false);
		} else {
			if (responseFromApi?.statusCode === BAD_REQUEST) {
				dispatch(
					setToastMessage({
						message: APP_GENERIC_BAD_REQUEST_MSG,
						severity: 'error',
						isToastOpen: true,
					}),
				);
			} else if (responseFromApi?.statusCode === NOT_FOUND) {
				dispatch(setCurrentWorkflowStatus('Not started'));
			} else {
				dispatch(
					setToastMessage({
						message: APP_GENERAL_ERROR_MSG,
						severity: 'error',
						isToastOpen: true,
					}),
				);
			}
			setIsLoading(false);
		}
	};

	useEffect(() => {
		fetchRegDetailsData();
		fetchWorkflowData();
	}, [subPartId]);

	const getPathForNavigation = (routePath: string) => {
		if (routePath) {
			return routePath === HOME_PAGE ? `${parent}` : `/${horizonScanning}`;
		}
		return `/${horizonScanning}`;
	};

	const path = urlWithTenant(getPathForNavigation(location?.state?.routePath));

	useEffect(() => {
		if (taskRowData && Object?.keys(taskRowData)?.length > 0) {
			const mappedData = taskTableMappedData?.filter(
				(data: ITaskTabContent) => data?.id === taskRowData?.id,
			);
			dispatch(
				setSelectedEntityTypeData({
					id: taskRowData.assignedObjectId,
					title: mappedData[0]?.mappedTo,
					type: taskRowData?.assignedObjectType?.toLowerCase(),
				}),
			);
		}
	}, [taskRowData, taskTableMappedData]);
	const truncateTitle =
		regDetailsData?.title && regDetailsData?.title?.length > TITLE_LENGTH
			? styles['horizon-detail-truncate-title']
			: '';

	const breadCrumbs = [
		<Link key="horizon-scanning" to={path} className={styles.link}>
			{location?.state?.routePath && location?.state?.routePath === HOME_PAGE
				? HOME_PAGE
				: HorizonScanningPageTitle.title}
		</Link>,
		<Typography key="horizon-details" className={truncateTitle}>
			{regDetailsData?.title || ''}
		</Typography>,
	];

	const handleToastHide = useCallback(() => {
		dispatch(
			setToastMessage({
				message: '',
				severity: '',
				isToastOpen: false,
			}),
		);
	}, []);

	const setCommentsSection = (data: boolean) => {
		setOpenCommentBox(data);
	};

	const makeAPICall = (data: boolean) => {
		if (data) {
			fetchRegDetailsData();
			fetchWorkflowData();
		}
	};
	useEffect(
		() => () => {
			dispatch(setIsRelationshipMappingDeleted(false));
			scrollToTop();
		},
		[],
	);

	useEffect(() => {
		const pageObj = {
			pageSize: TABLE_SIZE_12,
			pageNumber: 0,
		};
		updateTableQuery(pageObj);
	}, []);

	const currentWorkflowStatus = useSelector(
		(state: RootState) => state.horizonScan.currentWorkflowStatus,
	);

	return (
		<Stack>
			{loading ? (
				<CircularProgress className={styles['loading-icon']} />
			) : (
				<Stack spacing={3} data-testid="summary-content">
					<div className={styles['breadcrumbs-container']}>
						<div className={styles['breadcrumbs-wrapper']}>
							<Breadcrumbs
								breadcrumbs={breadCrumbs}
								className={horizonCommonStyles['bread-crumbs']}
								customClass
							/>
						</div>
					</div>
					<Banner>
						<Stack
							p={2}
							className={horizonCommonStyles['horizon-header-wrapper']}
						>
							<Typography
								className={horizonCommonStyles['horizon-banner-heading']}
							>
								{regDetailsData?.title || ''}
							</Typography>
							<RegHeader
								data={regDetailsData}
								headerItems={regHeaders}
								taskId={regDetailsData?.taskId}
								title={regDetailsData?.title}
							/>
						</Stack>
					</Banner>
					<StepperProgressBar
						disabled
						status={capitalizeEachWord(currentWorkflowStatus)}
						progressBarElements={getHorizonProgressBarElements}
						aria-label="Progress-Bar"
						step={null}
						valueLabelDisplay="off"
					/>
					<Grid container spacing={2}>
						<Grid item xs={12} className={styles['grid-item']}>
							<Typography variant="h6" gutterBottom>
								Please validate neccessary changes.
							</Typography>
						</Grid>
						<Grid item xs={4} className={styles['grid-item']}>
							<AISummary
								subPartId={subPartId}
								data={regDetailsData}
								isLoading={isRegDataLoading}
							/>
							<div className={styles['disclaimer-wrapper']}>
								<div className={styles['disclaimer-inner-wrapper']}>
									<Typography className={styles['disclaimer-text']}>
										{DISCLAIMER}
									</Typography>
								</div>
								<AIFeedback
									objectType={HORIZON_OBLIGATION_SUMMARY}
									module={HORIZON_SCAN_AND_ALERTS}
									objectData={{
										prompt: regDetailsData?.informationText,
										response: regDetailsData?.aiSummary,
										reference: subPartId,
									}}
									isDisabled={
										regDetailsData?.assignedUserId !== loggedInUser?.userId
									}
									shouldResetFeedback={isSummaryRegenerated}
								/>
							</div>
						</Grid>
						<Grid item xs={8} className={styles['grid-item']}>
							<DocumentSection
								commentsSectionRef={commentsSectionRef}
								scrollToCommentsSection={scrollToCommentsSection}
								data={regDetailsData}
								openCommentsSection={setCommentsSection}
								makeAPICall={makeAPICall}
								isLoading={isRegDataLoading}
							/>
						</Grid>
					</Grid>
					<div ref={commentsSectionRef} />
					{openCommentBox && !isRegDataLoading && (
						<CommentsSection data={regDetailsData} makeAPICall={makeAPICall} />
					)}
					<Divider />
					<ActionPlanLayout
						id={subPartId}
						data={regDetailsData}
						makeAPICall={makeAPICall}
						moduleType={HORIZON_SCAN_AND_ALERTS}
					/>
					<AlertComponent
						open={isToastOpen ?? false}
						toastMessage={message ?? ''}
						severity={severity ?? ''}
						handleCloseMessage={handleToastHide}
						vertical="top"
						horizontal="right"
						data-testid="alert-component"
					/>
				</Stack>
			)}
		</Stack>
	);
};

export default HorizonDetailLayout;
